import React from "react";
import { useTranslation } from "react-i18next";
import { Header, Segment, Message, Container, Button } from "semantic-ui-react";
import { useStore } from "../../../stores/store";

interface Props {
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
}

export default function PreSurveyIntroductionPage({ onNavigateNext, onNavigatePrevious }: Props) {
  const { t } = useTranslation("common");
  const { commonStore } = useStore();

  const content_en = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> Introduction</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong> will help you to understand your level of competencies leading to
        identification of professional development needs. The tool provides you with a comprehensive
        feedback report with practical suggestions to improve your digital competence as a teacher. It
        also includes a glossary of key terms (See navigation at top right of the tool) related to
        questions and feedback items. It is expected that you can finish in 25 minutes.
      </div>
      <div className="pre-post-txt">
        The data collected is treated with complete confidentiality and anonymity.
      </div>
      <div className="pre-post-txt">
        The questions and answers are linked to increasing levels of competence. Choose the answer that
        best fits. Once you have submitted your answers, you can download a feedback report indicating
        your level from Digital Novice to Digital Innovator.
      </div>
      <Message color="yellow">
        <strong>IMPORTANT: </strong>
        Do not close your browser window until you have selected ‘Submit’ on the final page of the
        self-assessment. Otherwise, your inputs may be lost.
      </Message>
    </>
  );

  const content_kh = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> សេចក្តីផ្តើម</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong>នឹងជួយលោកគ្រូ អ្នកគ្រូឱ្យដឹងពីកម្រិតនៃសមត្ថភាពឌីជីថល
        និងកំណត់បានពីតម្រូវការជាក់ស្តែងក្នុងការអភិវឌ្ឍវិជ្ជាជីវៈរបស់ខ្លួន។
        ឧបករណ៍នេះនឹងជួយផ្តល់ជារបាយការណ៍មតិត្រឡប់ក្នុងការអភិវឌ្ឈសមត្ថភាពឌីជីថលក្នុងអាជីពជាគ្រូបង្រៀន។
        ក្នុងឧបករណ៍នេះ ក៏មានភ្ជាប់មកជាមួយផងដែរនូវសទ្ទានុក្រមពាក្យគន្លឹះមានប្រើក្នុងកម្រងសំណួរ
        និងរបាយការណ៍មតិត្រឡប់។ ការធ្វើស្វ័យវាយតម្លៃនេះ ត្រូវការរយៈពេលប្រហែល ២៥ នាទី។
      </div>
      <div className="pre-post-txt">
        ការស្ទង់មតិនឹងត្រូវធ្វើឡើងដោយមានការរក្សាការសម្ងាត់ ហើយចម្លើយដែលអ្នកផ្តល់ឱ្យ គឺមានភាពអនាមិក
        និងមិនមានបញ្ចេញឈ្មោះរបស់អ្នកឡើយ។
      </div>
      <div className="pre-post-txt">
        គួរកត់សម្គាល់ផងដែរថាក្នុងផ្នែកនីមួយៗលេខសំណួរកាន់តែច្រើន កម្រិតសមត្ថភាពកាន់តែខ្ពស់
        ហើយជម្រើសចម្លើយក៏មានលក្ខណៈដូចគ្នានេះដែរ។ សូមជ្រើសរើសចម្លើយណាមួយត្រូវនឹងស្ថានភាពរបស់អ្នកជាងគេ។
        ពេលអ្នកបញ្ចប់គ្រប់សំណួរ សូមផ្ញើចេញ
        បន្ទាប់មកអ្នកអាចទាញយករបាយការណ៍មតិត្រឡប់បង្ហាញពីកម្រិតសមត្ថភាពឌីជីថល គិតពីកម្រិតអ្នកចាអ្នកប្រើប្រាស់ឌីជីថលថ្មីថ្មោង ទៅ អ្នកនវានុវត្តន៍ឌីជីថល
      </div>

      <Message color="yellow">
        <strong>សំខាន់៖ </strong>
        សូមកុំបិទផ្ទាំងកម្មវិធីរហូតទល់តែអ្នកបានផ្ញើចេញ (Submit) នៅទំព័រចុងក្រោយនៃឧបករណ៍
        បើមិនដូចនេះទេរាល់ព័ត៌មានអ្នកបានបំពេញនឹងបាត់បង់។
      </Message>
    </>
  );

  const content_vn = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> Giới thiệu</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong> sẽ giúp bạn xác định năng lực của mình để định hình nhu cầu phát triển chuyên môn. Công cụ này cung cấp cho bạn báo cáo phản hồi toàn diện với các đề xuất thiết thực để cải thiện năng lực kỹ thuật số của bạn với vai trò là giáo viên. Nó cũng bao gồm bảng giải thích các thuật ngữ chính (Xem điều hướng ở trên cùng bên phải của công cụ) liên quan đến các câu hỏi và mục phản hồi. Dự kiến bạn có thể hoàn thành nó trong 25 phút.
      </div>
      <div className="pre-post-txt">
      Dữ liệu thu thập được xử lý hoàn toàn bảo mật và ẩn danh.
      </div>
      <div className="pre-post-txt">
      Các câu hỏi và câu trả lời có tính liền mạch với mức độ năng lực tăng dần. Hãy chọn câu trả lời phù hợp nhất. Sau khi gửi câu trả lời, bạn có thể tải xuống báo cáo cho biết cấp độ của bạn từ Người mới làm quen kỹ thuật số đến Người đổi mới kỹ thuật số.
      </div>
      <Message color="yellow">
        <strong>QUAN TRỌNG: </strong>
        Không đóng cửa sổ trình duyệt của bạn cho đến khi bạn chọn 'Gửi' trên trang cuối cùng của bản tự đánh giá. Nếu không, dữ liệu của bạn có thể bị mất.
      </Message>
    </>
  );

  const content_id = (
    <>
      <Segment inverted color="blue" className="survey-intro-header">
        <Header as="h1"> Perkanalan</Header>
      </Segment>
      <div className="pre-post-txt">
        <strong>RefleCT </strong> akan membantu Anda memahami tingkat kompetensi yang mengarah pada identifikasi pengembangan profesional yang dibutuhkan. Alat ini memberikan Anda laporan umpan balik yang komprehensif dengan saran saran praktis untuk meningkatkan kompetensi digital Anda sebagai guru. Termasuk juga daftar istilah kata-kata kunci (lihat pojok kanan atas) terkait pertanyaan dan butir umpan balik. Diperkirakan Anda dapat menyelesaikan dalam 25 menit.
      </div>
      <div className="pre-post-txt">
      Data yang dikumpulkan akan diperlakukan secara rahasia dan anonim.
      </div>
      <div className="pre-post-txt">
      Pertanyaan dan jawaban disertai tautan untuk meningkatkan kompetensi. Pilihlah jawaban yang paling sesuai. Setelah Anda mengirim jawaban, Anda dapat mengunduh laporan umpan balik yang mengidikasikan tingkatan Anda dari Pemula Digital sampai Inovator Digital.
      </div>
      <Message color="yellow">
        <strong>PENTING: </strong>
        Jangan menutup jendela peramban Anda sampai melakukan 'Submit' pada halaman akhir dari penilaian diri. Atau, isian Anda mungkin hilang.
      </Message>
    </>
  );
  return (
    <>
      {commonStore.lang === "en" && content_en}
      {commonStore.lang === "kh" && content_kh}
      {commonStore.lang === "vn" && content_vn}
      {commonStore.lang === "id" && content_id}
      <Container className="survey-btns">
        <Segment>
          <Button
            primary
            floated="left"
            onClick={() => {
              onNavigatePrevious();
            }}
          >
            {t("Common.Previous")}
          </Button>

          <Button
            primary
            floated="right"
            onClick={() => {
              onNavigateNext();
            }}
          >
            {t("Common.Next")}
          </Button>
        </Segment>
      </Container>
    </>
  );
}
